import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow, isToday, isTomorrow } from 'date-fns';
import { fr } from 'date-fns/locale';

/** Fusionne des classes Tailwind en resolvant les conflits (p-2 + p-4 -> p-4). */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// --- Formatage ---------------------------------------------------------------

const euro = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

export function formatPrice(amount: number): string {
  return euro.format(amount);
}

/** « 1 h 30 » plutot que « 90 min » : plus lisible pour un client. */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')}`;
}

export function formatDate(value: string | Date): string {
  return format(new Date(value), 'd MMMM yyyy', { locale: fr });
}

export function formatDateShort(value: string | Date): string {
  return format(new Date(value), 'dd/MM/yyyy', { locale: fr });
}

export function formatTime(value: string | Date): string {
  return format(new Date(value), 'HH:mm', { locale: fr });
}

export function formatDateTime(value: string | Date): string {
  return format(new Date(value), "d MMMM yyyy 'a' HH:mm", { locale: fr });
}

/** « Aujourd'hui », « Demain », sinon la date complete. */
export function formatRelativeDay(value: string | Date): string {
  const date = new Date(value);
  if (isToday(date)) return "Aujourd'hui";
  if (isTomorrow(date)) return 'Demain';
  return format(date, 'EEEE d MMMM', { locale: fr });
}

export function formatTimeAgo(value: string | Date): string {
  return formatDistanceToNow(new Date(value), { addSuffix: true, locale: fr });
}

/** Creneau lisible : « 14:00 – 16:15 ». Le tiret demi-cadratin est la norme typographique. */
export function formatSlotRange(start: string | Date, end: string | Date): string {
  return `${formatTime(start)} – ${formatTime(end)}`;
}

export function fullName(
  person: { firstName: string; lastName: string } | null | undefined,
): string {
  if (!person) return 'Inconnu';
  return `${person.firstName} ${person.lastName}`.trim();
}

export function initials(
  person: { firstName: string; lastName: string } | null | undefined,
): string {
  if (!person) return '?';
  return `${person.firstName[0] ?? ''}${person.lastName[0] ?? ''}`.toUpperCase();
}

/** Lien d'itineraire, ouvert dans l'application de cartographie du telephone. */
export function directionsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}
